import React, { useId, useState, useEffect } from 'react';
import { useSmartScrollbarLayoutContext } from './SmartScrollbar';

// ── Dot-grid pattern constants ──────────────────────────────────
const DOT_SIZE = 2;
const DOT_GAP = 4;
const DOT_STEP = DOT_SIZE + DOT_GAP; // 6px
const DOT_RADIUS = DOT_SIZE / 2;
const FADE_DURATION_MS = 500;

interface SmartScrollbarTrackProps {
  className?: string;
  children?: React.ReactNode;
}

function DotGrid({ w, h, patternId }: { w: number; h: number; patternId: string }) {
  const dotColor = `hsl(var(--neutral) / 0.5)`;
  return (
    <svg
      width={w}
      height={h}
      className="absolute inset-0"
    >
      <defs>
        <pattern
          id={patternId}
          width={DOT_STEP}
          height={DOT_STEP}
          patternUnits="userSpaceOnUse"
        >
          <circle
            cx={DOT_RADIUS}
            cy={DOT_RADIUS}
            r={DOT_RADIUS}
            fill={dotColor}
          />
        </pattern>
        <clipPath id={`${patternId}-clip`}>
          <rect
            x={0}
            y={0}
            width={w}
            height={h}
          />
        </clipPath>
      </defs>
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        fill={`url(#${patternId})`}
        clipPath={`url(#${patternId}-clip)`}
      />
    </svg>
  );
}

export const SmartScrollbarTrack = React.memo(function SmartScrollbarTrack({
  className,
  children,
}: SmartScrollbarTrackProps) {
  const { trackHeight, effectiveWidth, isLoading } = useSmartScrollbarLayoutContext();
  const patternId = useId();

  // Keep the dot grid mounted long enough to fade out, then unmount entirely.
  const [dotGridMounted, setDotGridMounted] = useState(isLoading);
  useEffect(() => {
    if (isLoading) {
      setDotGridMounted(true);
      return;
    }
    const t = setTimeout(() => setDotGridMounted(false), FADE_DURATION_MS);
    return () => clearTimeout(t);
  }, [isLoading]);

  if (trackHeight === 0) return null;

  const w = effectiveWidth;
  const h = trackHeight;

  return (
    <div className={`absolute inset-0 ${className ?? ''}`}>
      {dotGridMounted && (
        <div
          className="absolute inset-0"
          style={{
            width: w,
            height: h,
            opacity: isLoading ? 1 : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease`,
          }}
        >
          <DotGrid
            w={w}
            h={h}
            patternId={patternId}
          />
        </div>
      )}
      {children}
    </div>
  );
});
